/*
 * THE DESKTOP DROPDOWN'S BEHAVIOUR (fz-desktop-menu.css has the why; Luke,
 * 2026-10-10: "the menu sub items actually appear below the main link that is
 * clicked").
 *
 * Horizon's header-menu.js stays in charge of state: aria-expanded on the
 * disclosure button, `inert` on the submenu, hover open and close, Escape. This
 * file only adds what live's dropdown has and Horizon's lacks, and it acts
 * through header-menu's public toggle(), so there is still one source of truth:
 *
 *   - CLICK OPENS. A top-level link that has a submenu toggles it, as live's
 *     button does, instead of navigating; the list's own first item ("Shop All")
 *     is the destination. A click on a list a hover already opened keeps it
 *     open (the pointer is on it, closing would read as the click failing), and
 *     the next click closes it. Modified clicks (new tab) still navigate.
 *   - A TAP STAYS OPEN. A touch pointer "leaves" as soon as the finger lifts,
 *     which made Horizon close the list it had just opened; that pointerleave is
 *     swallowed for touch, and a tap anywhere outside closes it instead.
 *   - ARROWS: Down/Up on a top-level link open its list and focus the first or
 *     last link; inside the list they move between the links a shopper can see
 *     (the market-hidden ones are display: none and are skipped), Home and End
 *     jump; Left/Right move along the top level. Space opens like Enter.
 *   - HOVER INTENT: a pointer gets the list after a short dwell (the CSS
 *     transition delay), so sweeping across the bar flashes nothing; a click or
 *     a key marks the header `data-fz-menu-instant` and the list shows at once.
 *   - ON SCREEN: each time a list opens it is measured and shifted left just
 *     enough to stay 16 px inside the window. The same for "More" (990-1179
 *     px), whose panel sits in overflow-list's shadow root.
 *
 * Every listener is delegated from the document (or the window) and every
 * element is looked up when the event arrives, because the header is
 * re-rendered (hydration) and nodes a script holds on to go stale.
 */
const DESKTOP = window.matchMedia("(min-width: 990px)");
const EDGE = 16;
const ITEM = ".menu-list__list-item:not([slot])";

/** @returns {HTMLElement & { toggle?: Function } | null} */
function menu() {
  const el = document.querySelector('#header-component[data-menu-style="menu"] header-menu');
  return DESKTOP.matches && el instanceof HTMLElement ? el : null;
}

/** @param {EventTarget | null} node */
function itemOf(node) {
  const m = menu();
  if (!m || !(node instanceof Element) || !m.contains(node)) return null;
  const li = node.closest(ITEM);
  return li instanceof HTMLElement && li.querySelector(':scope > [ref="submenu[]"]') ? li : null;
}

/** @param {HTMLElement} li */
const disclosure = (li) => li.querySelector(':scope > .menu-list__trigger [ref="disclosure[]"]');
/** @param {HTMLElement} li */
const isOpen = (li) => disclosure(li)?.getAttribute("aria-expanded") === "true";
/** @param {HTMLElement} li */
const topLink = (li) =>
  /** @type {HTMLElement | null} */ (
    li.querySelector(":scope > .menu-list__trigger .menu-list__link, :scope > .menu-list__link")
  );
/** @param {HTMLElement} li */
const subLinks = (li) =>
  /** @type {HTMLElement[]} */ ([...li.querySelectorAll(':scope > [ref="submenu[]"] a[href]')]).filter(
    (a) => a.getClientRects().length > 0,
  );

/** The top-level links a keyboard can reach, in order. */
function topLinks() {
  const m = menu();
  if (!m) return [];
  return /** @type {HTMLElement[]} */ ([...m.querySelectorAll(`${ITEM} .menu-list__link`)]).filter(
    (a) => a.getClientRects().length > 0 && !a.closest('[aria-hidden="true"]'),
  );
}

/** Which item the last click opened, so the next click on it closes it. */
/** @type {HTMLElement | null} */
let pinned = null;

/**
 * The item a click just closed, while the pointer is still on it. Horizon's
 * Safari workaround (#reconcilePointerTarget) re-sends `pointerenter` to the
 * item under a resting pointer, which reopened the list a click had closed. It
 * stays shut until the pointer really leaves.
 * @type {HTMLElement | null}
 */
let shut = null;

/**
 * @param {HTMLElement} li
 * @param {boolean} open
 */
function setOpen(li, open) {
  const m = menu();
  const button = disclosure(li);
  if (!m || !button || typeof m.toggle !== "function" || isOpen(li) === open) return;
  m.dataset.fzMenuInstant = "";
  m.toggle({ target: button });
}

function closeAll() {
  const m = menu();
  if (!m) return;
  for (const li of m.querySelectorAll(ITEM)) if (li instanceof HTMLElement && isOpen(li)) setOpen(li, false);
  pinned = null;
}

document.addEventListener(
  "click",
  (event) => {
    if (!(event instanceof MouseEvent) || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target instanceof Element ? event.target : null;
    const li = itemOf(target);
    if (!li || !target?.closest(".menu-list__link")) return;
    event.preventDefault();
    if (isOpen(li) && pinned !== li) {
      pinned = li;
      menu()?.setAttribute("data-fz-menu-instant", "");
      return;
    }
    const next = !isOpen(li);
    setOpen(li, next);
    pinned = next ? li : null;
    shut = next ? null : li;
  },
  true,
);

// On WINDOW: Horizon's component.js delegates every `on:` handler from the
// document in the capture phase, registered before this file runs, so a
// document listener here would run after it and could not stop it.
window.addEventListener(
  "pointerenter",
  (event) => {
    if (shut && event.target === shut) event.stopImmediatePropagation();
  },
  true,
);

window.addEventListener(
  "pointerleave",
  (event) => {
    if (itemOf(event.target) !== event.target) return;
    // A finger lifting is not the pointer leaving the menu.
    if (event.pointerType === "touch") event.stopImmediatePropagation();
    else if (event.isTrusted && event.target === shut) shut = null;
  },
  true,
);

document.addEventListener(
  "pointerdown",
  (event) => {
    const m = menu();
    if (!m) return;
    // A mouse inside the menu is handled by hover and the click above.
    if (event.pointerType === "mouse" && m.contains(/** @type {Node} */ (event.target))) return;
    const inside = itemOf(event.target);
    for (const li of m.querySelectorAll(ITEM)) {
      if (li instanceof HTMLElement && li !== inside && isOpen(li)) setOpen(li, false);
    }
    if (!inside) pinned = null;
  },
  true,
);

// A mouse moving over the page with nothing open is hover again: back to the dwell.
document.addEventListener(
  "pointerover",
  (event) => {
    if (event.pointerType !== "mouse") return;
    const m = menu();
    if (m && "fzMenuInstant" in m.dataset && !m.querySelector(`${ITEM} [aria-expanded="true"]`)) {
      delete m.dataset.fzMenuInstant;
    }
  },
  true,
);

document.addEventListener(
  "keydown",
  (event) => {
    const target = event.target instanceof HTMLElement ? event.target : null;
    const m = menu();
    if (!m || !target || !m.contains(target)) return;
    const li = target.closest(ITEM);
    if (!(li instanceof HTMLElement)) return;
    const hasList = Boolean(li.querySelector(':scope > [ref="submenu[]"]'));
    const inList = Boolean(target.closest('[ref="submenu[]"]'));
    const tops = topLinks();
    const here = tops.indexOf(/** @type {HTMLElement} */ (topLink(li)));

    /** @param {HTMLElement | undefined} el */
    const go = (el) => {
      if (!el) return;
      event.preventDefault();
      m.dataset.fzMenuInstant = "";
      el.focus();
    };

    switch (event.key) {
      case " ":
        if (inList || !hasList || !target.closest(".menu-list__link")) return;
        event.preventDefault();
        setOpen(li, !isOpen(li));
        pinned = isOpen(li) ? li : null;
        shut = null;
        return;
      case "ArrowDown":
      case "ArrowUp": {
        if (!hasList) return;
        event.preventDefault();
        if (!isOpen(li)) setOpen(li, true);
        const links = subLinks(li);
        const at = links.indexOf(target);
        const down = event.key === "ArrowDown";
        const next = at < 0 ? (down ? 0 : links.length - 1) : (at + (down ? 1 : -1) + links.length) % links.length;
        go(links[next]);
        return;
      }
      case "Home":
      case "End": {
        if (!inList) return;
        const links = subLinks(li);
        go(event.key === "Home" ? links[0] : links[links.length - 1]);
        return;
      }
      case "ArrowRight":
      case "ArrowLeft": {
        if (here < 0) return;
        const step = event.key === "ArrowRight" ? 1 : -1;
        const next = tops[(here + step + tops.length) % tops.length];
        if (inList) setOpen(li, false);
        go(next);
        return;
      }
    }
  },
  true,
);

/**
 * Hang an open list from the bar's bottom edge, and keep it inside the window by
 * shifting it left by what it overhangs.
 * @param {HTMLElement} li
 */
function place(li) {
  const box = li.querySelector(':scope > [ref="submenu[]"] > .menu-list__submenu-inner');
  const row = li.closest(".header__row");
  if (!(box instanceof HTMLElement)) return;
  if (row) {
    const gap = row.getBoundingClientRect().bottom - li.getBoundingClientRect().bottom;
    li.style.setProperty("--fz-dropdown-gap", `${Math.max(0, Math.round(gap * 10) / 10)}px`);
  }
  li.style.removeProperty("--fz-dropdown-offset");
  const anchor = li.getBoundingClientRect().left;
  const width = box.getBoundingClientRect().width;
  const room = document.documentElement.clientWidth - EDGE;
  let offset = 0;
  if (anchor + width > room) offset = room - width - anchor;
  if (anchor + offset < EDGE) offset = EDGE - anchor;
  if (offset) li.style.setProperty("--fz-dropdown-offset", `${Math.round(offset)}px`);
}

/**
 * Place the More panel (it lives in overflow-list's shadow root, absolutely
 * positioned against an ancestor of the More item, not the item): under More,
 * at the bar's bottom edge, inside the window.
 */
function placeMore() {
  const m = menu();
  const host = m?.querySelector("overflow-list");
  const more = m?.querySelector('.menu-list__list-item[slot="more"]');
  const panel = host?.shadowRoot?.querySelector('[part="overflow"]');
  const box = host?.shadowRoot?.querySelector('[part="overflow-list"]');
  const row = m?.closest(".header__row");
  if (!(host instanceof HTMLElement) || !more || !(panel instanceof HTMLElement) || !box || !row) return;
  const base = panel.offsetParent ?? document.documentElement;
  const origin = base.getBoundingClientRect();
  const anchor = more.getBoundingClientRect().left;
  const width = box.getBoundingClientRect().width;
  const room = document.documentElement.clientWidth - EDGE;
  const left = Math.max(EDGE, Math.min(anchor, room - width));
  host.style.setProperty("--fz-more-left", `${Math.round(left - origin.left)}px`);
  host.style.setProperty(
    "--fz-more-top",
    `${Math.round((row.getBoundingClientRect().bottom - origin.top) * 10) / 10}px`,
  );
}

new MutationObserver((records) => {
  const m = menu();
  if (!m) return;
  // More, or an item inside it, changed: its box may have grown a column.
  if (m.querySelector('.menu-list__list-item[slot="more"] [aria-expanded="true"]')) requestAnimationFrame(placeMore);
  for (const record of records) {
    const button = record.target;
    if (!(button instanceof HTMLElement) || button.getAttribute("ref") !== "disclosure[]") continue;
    const li = itemOf(button);
    if (!li) continue;
    if (button.getAttribute("aria-expanded") === "true") requestAnimationFrame(() => place(li));
    else if (pinned === li) pinned = null;
  }
}).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ["aria-expanded"] });
